'use client';

/*
 * "expo-router" for the website. The app's screens only use a small part of it (router.push,
 * router.back, links, route params), so this is Next.js's own router wearing expo-router's API.
 * Where a screen says router.push('/(tabs)/discover') it goes to /discover, exactly as on the phone.
 */
import React, { useEffect } from 'react';
import NextLink from 'next/link';
import { useParams, usePathname as useNextPathname, useRouter as useNextRouter, useSearchParams } from 'next/navigation';

// The app navigates from outside components too (event handlers, thunks), so the Next.js router is
// kept in a module-level holder that <RouterBridge /> fills in.
const holder = { router: null, pathname: '/', search: '' };

/** Tab and auth groups are folders in the app but not part of an address. */
export function toPath(href) {
  if (href == null) return '/';
  let pathname;
  let params = {};
  if (typeof href === 'string') {
    const [p, query] = href.split('?');
    pathname = p;
    if (query) params = Object.fromEntries(new URLSearchParams(query));
  } else {
    pathname = href.pathname || '/';
    params = { ...(href.params || {}) };
    if (href.query) params = { ...params, ...href.query };
  }

  // Fill [id] style placeholders from params; whatever is left becomes the query string.
  pathname = pathname.replace(/\[(?:\.\.\.)?([^\]]+)\]/g, (_, name) => {
    const value = params[name];
    delete params[name];
    return encodeURIComponent(String(value ?? ''));
  });

  const hadTabs = /\(tabs\)/.test(pathname);
  pathname = pathname.replace(/\/\((?:auth|tabs)\)/g, '').replace(/\/index$/, '') || '/';
  // The Home tab is the group's index, and "/" itself is the splash screen.
  if (hadTabs && pathname === '/') pathname = '/home';

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== null) search.set(key, String(value));
  const qs = search.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export const router = {
  push: (href) => holder.router?.push(toPath(href)),
  replace: (href) => holder.router?.replace(toPath(href)),
  navigate: (href) => holder.router?.push(toPath(href)),
  back: () => (typeof window !== 'undefined' && window.history.length > 1 ? holder.router?.back() : holder.router?.replace('/home')),
  dismiss: () => router.back(),
  canGoBack: () => typeof window !== 'undefined' && window.history.length > 1,
  setParams: (params) => {
    const next = new URLSearchParams(holder.search);
    for (const [key, value] of Object.entries(params || {})) {
      if (value === undefined || value === null) next.delete(key);
      else next.set(key, String(value));
    }
    const qs = next.toString();
    holder.router?.replace(`${holder.pathname}${qs ? `?${qs}` : ''}`);
  },
};

export function useRouter() {
  return router;
}

/** Mount once, near the root. */
export function RouterBridge() {
  const nextRouter = useNextRouter();
  const pathname = useNextPathname();
  const search = useSearchParams();
  holder.router = nextRouter;
  holder.pathname = pathname;
  holder.search = search?.toString() || '';
  return null;
}

export function usePathname() {
  return useNextPathname();
}

/** The route's own parameters ([id]) together with its query string, as expo-router's hook returns them. */
export function useLocalSearchParams() {
  const params = useParams();
  // Next's useSearchParams makes a statically built page give up on server rendering everything around the component that
  // calls it: the page arrived as an empty shell and its content only appeared after the browser had downloaded and run
  // all the JavaScript. So the server does not call it (a screen is built there with no query), and the browser always does,
  // so a screen's first render in the browser, and every navigation inside the app, sees the real address. (typeof window is
  // fixed for a whole run, so the hook order never changes.)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const search = typeof window === 'undefined' ? null : useSearchParams();
  const out = {};
  for (const [key, value] of Object.entries(params || {})) out[key] = Array.isArray(value) ? value.map(decodeURIComponent) : decodeURIComponent(value);
  search?.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}
export const useGlobalSearchParams = useLocalSearchParams;

export function useSegments() {
  const pathname = useNextPathname() || '/';
  const parts = pathname.split('/').filter(Boolean);
  if (!parts.length) return [];
  if (['home', 'discover', 'bookings', 'favorites', 'profile'].includes(parts[0])) return ['(tabs)', parts[0] === 'home' ? 'index' : parts[0]];
  if (['welcome', 'onboarding', 'login', 'register', 'otp-verification', 'forgot-password', 'reset-password', 'location-permission'].includes(parts[0])) return ['(auth)', ...parts];
  return parts;
}

/** Runs when the screen is shown. (Screens here are mounted fresh on every visit.) */
export function useFocusEffect(effect) {
  useEffect(() => {
    const cleanup = effect();
    return typeof cleanup === 'function' ? cleanup : undefined;
  }, [effect]);
}

export function useNavigation() {
  return { goBack: () => router.back(), navigate: (name) => router.push(name), setOptions: () => undefined, addListener: () => () => undefined };
}

export function Link({ href, asChild, children, replace, onPress, ...rest }) {
  const path = toPath(href);
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      href: path,
      onPress: (event: any) => {
        (children.props as any).onPress?.(event);
        onPress?.(event);
        if (event?.defaultPrevented) return;
        if (replace) router.replace(href);
        else router.push(href);
      },
    });
  }
  return (
    <NextLink href={path} replace={replace} onClick={onPress} {...rest}>
      {children}
    </NextLink>
  );
}

// Layout components exist in the app's route files, which the website replaces with Next.js layouts.
export const Stack = Object.assign(({ children }) => <>{children}</>, { Screen: () => null });
export const Tabs = Object.assign(({ children }) => <>{children}</>, { Screen: () => null });
export const Slot = ({ children }) => <>{children}</>;
export const Redirect = ({ href }) => {
  useEffect(() => {
    router.replace(href);
  }, [href]);
  return null;
};
export function ErrorBoundary({ error, retry }) {
  return (
    <div style={{ padding: 24 }}>
      <p>{String(error?.message || 'Something went wrong.')}</p>
      <button type="button" onClick={retry}>
        Try again
      </button>
    </div>
  );
}
